import type { Ring, Vec2 } from './types';

const BAND = 2;

/** A ring's edges filed by the height bands they cross, as xi, yi, xj, yj runs. */
export interface BandedRing {
  readonly minY: number;
  readonly bands: readonly (readonly number[])[];
}

export function bandRing(ring: Ring): BandedRing {
  const ys = ring.map((p) => p[1]);
  const minY = Math.min(...ys);
  const bands: number[][] = Array.from(
    { length: Math.floor((Math.max(...ys) - minY) / BAND) + 1 },
    () => []
  );
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    const last = Math.floor((Math.max(yi, yj) - minY) / BAND);
    for (let b = Math.floor((Math.min(yi, yj) - minY) / BAND); b <= last; b++)
      bands[b]!.push(xi, yi, xj, yj);
  }
  return { minY, bands };
}

/** The pointInRing crossing test, in the same edge order, restricted to the point's height band. */
export function inBandedRing(p: Vec2, ring: BandedRing): boolean {
  const edges = ring.bands[Math.floor((p[1] - ring.minY) / BAND)];
  if (!edges) return false;
  let inside = false;
  for (let k = 0; k < edges.length; k += 4) {
    const xi = edges[k]!;
    const yi = edges[k + 1]!;
    const xj = edges[k + 2]!;
    const yj = edges[k + 3]!;
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}
